import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { DealRoom, DealRoomTopNodes } from 'models/DealRoom';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableCard from 'components/DraggableCard';
import DragIcon from 'icons/Drag.icon';
import { Button } from 'ncoded-component-library';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import { getNodeMimeType } from 'utils';
import useNodeInfo from 'components/FileAccessSelector/hooks/useNodeInfo';
import DataRoomNodeTagAndVersion from 'components/DataRoomNodeTagAndVersion';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useReordering from './hooks/useReordering';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import DataRoomNodeCurrentLocation from 'components/DataRoomNodeCurrentLocation';

import './ReorderFilesDrawer.styles.scss';
import './ReorderFilesDrawer.styles.responsive.scss';

type ReorderFilesDrawerProps = {
  dealRoom: DealRoom;
  dealRoomTopNodes: DealRoomTopNodes[];
  onClose: (dataRoomTopNodesAfterReorder: DealRoomTopNodes[]) => void;
} & SideDrawerProps;

const ReorderFilesDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ReorderFilesDrawerProps
> = (props, ref) => {
  const { children, className, dealRoom, dealRoomTopNodes, onClose, ...rest } =
    props;

  const { _id: dealRoomId, name } = dealRoom;

  const { openNode } = useContext(DocumentViewContext);

  const { isReordering, items, reorderButtonEvents, onDragEnd } = useReordering(
    dealRoomId,
    dealRoomTopNodes,
  );

  const dataRoomTopNodesAfterReorder = useMemo(
    () =>
      items.map((dataRoomNode, index) => ({
        dataRoomNode,
        orderNumber: index,
        dataRoomNodeId: dataRoomNode._id,
      })),
    [items],
  );

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('reorder-files-drawer', className);

  const { t } = useTranslation();

  const createInfoData = useNodeInfo(false);

  return (
    <SideDrawer
      ref={drawerRef}
      closeAction={() => onClose(dataRoomTopNodesAfterReorder)}
      className={classes}
      {...rest}
    >
      <div className="header">
        <p>{t('fileAccessLC')}</p>
        <p>{t('reorderFilesDisplayedWithingLeadInformation')}</p>
      </div>

      <div className="content">
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((node) => (
              <DraggableCard
                key={node._id}
                id={node._id}
                dragEnabled={isReordering}
              >
                <div className="draggable">
                  <div className="draggable__left">
                    <Button
                      icon={DragIcon}
                      variant="icon"
                      {...reorderButtonEvents}
                    />
                  </div>

                  <div
                    className="draggable__right clickable"
                    onClick={() => openNode(node, name)}
                  >
                    <div>
                      <MymeTypeIcon mimeType={getNodeMimeType(node)} />

                      <div className="node-wrapper">
                        <div>
                          <p className="name">
                            <span>{node.name}</span>
                            <DataRoomNodeTagAndVersion
                              tag={node.tag}
                              version={node.version}
                            />
                          </p>

                          {createInfoData(node as DataRoomNode)}
                        </div>
                        <DataRoomNodeCurrentLocation
                          dataRoomId={node.dataRoomId}
                          currentLocation={{
                            parentId: node.parentId || null,
                            parentName: node.parentName,
                          }}
                          onLinkClick={() => drawer?.setOpen(false)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </DraggableCard>
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </SideDrawer>
  );
};

export default forwardRef(ReorderFilesDrawer);
